/**
 * Funnel chart builder (ADR-0079 §4.1, §4.4).
 *
 * One funnel slice per row. `options.sort` controls slice ordering: "desc"
 * (default — biggest at top), "asc", or "none" (preserve row order).
 */

import type { FunnelSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  buildTooltipFormatter,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

export function buildFunnelOption(
  spec: FunnelSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const categoryIdx = resolveColumnIndex(spec.encoding.category.column, dimensions);
  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);

  const data: Array<{ name: string; value: number | null }> =
    categoryIdx === null || valueIdx === null
      ? []
      : rows.map((row) => ({
          name: row[categoryIdx] == null ? "" : String(row[categoryIdx]),
          value: coerce(row[valueIdx]),
        }));

  const sort = spec.options.sort ?? "desc";
  const labelPosition = spec.options.labelPosition ?? "inside";
  const palette = resolvePalette(spec.options.color, data.length);
  const tooltipFormatter = buildTooltipFormatter(
    spec.options.tooltip,
    [{ name: spec.encoding.value.column }],
    spec.encoding.value.format,
  );

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
    },
    series: [
      {
        type: "funnel",
        name: spec.encoding.value.column,
        sort,
        data,
        label: { show: true, position: labelPosition },
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
